import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  CatalogClient,
  GetEntitiesRequest,
  GetEntityFacetsRequest,
  CatalogRequestOptions,
} from '@backstage/catalog-client';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

/**
 * Returns the best available display name for an entity, used for alphabetical sorting.
 * Matches the priority order used by the entityPresentationApi (displayName > title > name).
 */
function displayNameOf(entity: Entity): string {
  const profile = entity.spec?.profile as { displayName?: string } | undefined;
  return profile?.displayName ?? entity.metadata.title ?? entity.metadata.name;
}

/**
 * Wraps CatalogClient to return entities sorted alphabetically by display name
 * on every getEntities call. This ensures all OwnerPicker / EntityPicker
 * dropdowns in the scaffolder are consistently alphabetized without any
 * per-template changes.
 */
class SortingCatalogClient extends CatalogClient {
  override async getEntities(
    request?: GetEntitiesRequest,
    options?: CatalogRequestOptions,
  ) {
    const response = await super.getEntities(request, options);
    response.items.sort((a, b) =>
      displayNameOf(a).localeCompare(displayNameOf(b), undefined, { sensitivity: 'base' }),
    );
    return response;
  }

  override async getEntityFacets(
    request: GetEntityFacetsRequest,
    options?: CatalogRequestOptions,
  ) {
    const response = await super.getEntityFacets(request, options);
    for (const values of Object.values(response.facets)) {
      values.sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }),
      );
    }
    return response;
  }
}

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: catalogApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new SortingCatalogClient({ discoveryApi, fetchApi }),
  }),
];
